import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, Label,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    AreaChart, Area
} from 'recharts';
import {
    BarChart2,
    CheckCircle2,
    TrendingUp,
    FileSpreadsheet,
    Trophy,
    User,
    MapPin,
    AlertCircle,
    ClipboardList,
    Zap,
    Activity,
    Target,
    Globe,
    Clock,
    X,
    Sparkles,
    XCircle
} from 'lucide-react';
import API_URL from '../api';
import AiReportView from './AiReportView';
import './Dashboard.css';

/* ── helpers ─────────────────────────────────────── */
const getResultFirstName  = (r) => r?.studentName      || r?.playerName      || '';
const getResultLastName   = (r) => r?.studentLastName  || r?.playerLastName  || '';
const getResultCity       = (r) => r?.studentCity      || r?.playerCity      || r?.city       || '';
const getResultPosition   = (r) => r?.studentPosition  || r?.playerPosition  || r?.position   || '';
const getResultFullName   = (r) => `${getResultLastName(r)} ${getResultFirstName(r)}`.trim();

const getTypeLabel = (type) => ({
    test:    'Сервірування',
    game:    'Гра',
    quiz:    'Квіз',
    complex: 'Комплексний',
    pizza:   'Піца',
    course:  'Курс'
}[type] || type || '—');

const getTypeBadgeClass = (type) => ({
    test:    'badge-test',
    game:    'badge-game',
    quiz:    'badge-quiz',
    complex: 'badge-complex',
    pizza:   'badge-pizza',
    course:  'badge-course'
}[type] || '');

const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Доброго ранку';
    if (h < 18) return 'Добрий день';
    return 'Добрий вечір';
};

const timeAgo = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr)) / 1000;
    if (diff < 60)   return 'щойно';
    if (diff < 3600) return `${Math.floor(diff / 60)} хв тому`;
    if (diff < 86400)return `${Math.floor(diff / 3600)} год тому`;
    return `${Math.floor(diff / 86400)} д тому`;
};

const autoWidth = (worksheet, rows) => {
    if (!rows.length) return;
    const keys = Object.keys(rows[0]);
    worksheet['!cols'] = keys.map((key) => {
        const maxLength = Math.max(key.length, ...rows.map((row) => String(row[key] ?? '').length));
        return { wch: Math.min(maxLength + 2, 40) };
    });
};

/* ── AnimatedCounter ─────────────────────────────── */
const AnimatedCounter = ({ target, suffix = '' }) => {
    const [display, setDisplay] = useState(0);
    const raf = useRef(null);

    useEffect(() => {
        const numTarget = parseFloat(String(target).replace('%', '')) || 0;
        const duration = 900;
        const start = performance.now();
        const animate = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplay(Math.round(eased * numTarget));
            if (progress < 1) raf.current = requestAnimationFrame(animate);
        };
        raf.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(raf.current);
    }, [target]);

    return <>{display}{suffix}</>;
};

/* ── Main Component ──────────────────────────────── */
const Dashboard = ({ user }) => {
    const [stats, setStats] = useState([
        { label: 'Всього результатів', value: 0,    suffix: '',  icon: <BarChart2   size={22} />, color: 'blue'   },
        { label: 'Успішно',            value: 0,    suffix: '',  icon: <CheckCircle2 size={22} />, color: 'green'  },
        { label: 'Провалено',          value: 0,    suffix: '',  icon: <XCircle      size={22} />, color: 'red'    },
        { label: 'Середній %',         value: 0,    suffix: '%', icon: <TrendingUp   size={22} />, color: 'orange' },
        { label: 'Міст / Філіалів',    value: 0,    suffix: '',  icon: <Globe        size={22} />, color: 'purple' },
    ]);

    const [recentResults, setRecentResults]     = useState([]);
    const [passingData,   setPassingData]       = useState([]);
    const [categoryData,  setCategoryData]      = useState([]);
    const [trendData,     setTrendData]         = useState([]);
    const [weakSpots,     setWeakSpots]         = useState([]);
    const [leaderboard,   setLeaderboard]       = useState([]);
    const [statsPayload,  setStatsPayload]      = useState(null);
    const [loading,       setLoading]           = useState(true);
    const [selectedCity,  setSelectedCity]      = useState('');
    const [cities,        setCities]            = useState([]);
    const [period,        setPeriod]            = useState(30);
    const [activeTab,     setActiveTab]         = useState('overview');

    const [aiAnalysis,  setAiAnalysis]  = useState('');
    const [aiSummary,   setAiSummary]   = useState(null);
    const [aiLoading,   setAiLoading]   = useState(false);
    const [aiOpen,      setAiOpen]      = useState(false);

    const isAdminCityOnly = user?.role !== 'superadmin' && user?.city;

    /* ── data fetching (unchanged) ── */
    const fetchCities = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/stats/cities`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCities(res.data);
        } catch (err) {
            console.error('Failed to fetch cities:', err);
        }
    }, []);

    const fetchStats = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };

            const queryParams = new URLSearchParams();
            if (selectedCity) queryParams.append('city', selectedCity);
            queryParams.append('days', period);

            const statsRes = await axios.get(`${API_URL}/stats/overview?${queryParams}`, config);
            const data = statsRes.data;
            setStatsPayload(data);

            setStats([
                { label: 'Всього результатів', value: data.totalResults,       suffix: '',  icon: <BarChart2    size={22} />, color: 'blue'   },
                { label: 'Успішно',            value: data.passedResults,      suffix: '',  icon: <CheckCircle2  size={22} />, color: 'green'  },
                { label: 'Провалено',          value: data.failedResults,      suffix: '',  icon: <XCircle       size={22} />, color: 'red'    },
                { label: 'Середній %',         value: data.overallPercentage,  suffix: '%', icon: <TrendingUp    size={22} />, color: 'orange' },
                { label: 'Міст / Філіалів',    value: data.byCity?.length || 0,suffix: '',  icon: <Globe         size={22} />, color: 'purple' },
            ]);

            setPassingData([
                { name: 'Успішно',  value: data.passedResults, color: '#4ade80' },
                { name: 'Провалено',value: data.failedResults,  color: '#f87171' }
            ]);

            setCategoryData([
                { name: 'Сервірування', value: data.byType.test    || 0 },
                { name: 'Гра',          value: data.byType.game    || 0 },
                { name: 'Квіз',         value: data.byType.quiz    || 0 },
                { name: 'Комплексний',  value: data.byType.complex || 0 },
                { name: 'Піца',         value: data.byType.pizza   || 0 },
                { name: 'Курси',        value: data.byType.course  || 0 },
            ]);

            setTrendData(data.chartData);
            setWeakSpots(data.weakSpots);
            setRecentResults(data.recentResults);
            setLeaderboard(data.leaderboard || []);

        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setLoading(false);
        }
    }, [period, selectedCity]);

    useEffect(() => {
        if (!isAdminCityOnly && user?.role === 'superadmin') {
            fetchCities();
        }
        fetchStats();
    }, [fetchCities, fetchStats, isAdminCityOnly, user?.role]);

    /* ── AI (unchanged) ── */
    const handleAiAnalyze = async () => {
        setAiLoading(true);
        setAiOpen(true);
        setAiAnalysis('');
        setAiSummary(null);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_URL}/ai/analyze`, {
                mode: 'dashboard',
                city: selectedCity || undefined,
                days: period       || undefined,
                tab:  'all',
            }, { headers: { Authorization: `Bearer ${token}` } });
            setAiAnalysis(res.data.analysis);
            setAiSummary(res.data.summary || null);
        } catch (err) {
            setAiAnalysis('Помилка: ' + (err.response?.data?.error || err.message));
        } finally {
            setAiLoading(false);
        }
    };

    /* ── Export (unchanged) ── */
    const handleExportStats = () => {
        if (!statsPayload) return;
        const workbook     = XLSX.utils.book_new();
        const generatedAt  = new Date();
        const cityLabel    = selectedCity || user?.city || 'Всі доступні';

        const summaryRows = [
            { Показник: 'Звіт сформовано',      Значення: generatedAt.toLocaleString('uk-UA') },
            { Показник: 'Місто',                 Значення: cityLabel },
            { Показник: 'Період',                Значення: `${period} днів` },
            { Показник: 'Всього результатів',    Значення: statsPayload.totalResults },
            { Показник: 'Успішно',               Значення: statsPayload.passedResults },
            { Показник: 'Провалено',             Значення: statsPayload.failedResults },
            { Показник: 'Середній результат %',  Значення: statsPayload.averagePercentage ?? statsPayload.overallPercentage },
            { Показник: 'Успішність %',          Значення: statsPayload.successRate ?? 0 },
        ];
        const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
        autoWidth(summarySheet, summaryRows);
        XLSX.utils.book_append_sheet(workbook, summarySheet, 'Зведення');

        const typeRows = [
            { Категорія: 'Сервірування', Кількість: statsPayload.byType?.test    || 0, 'Середній результат %': statsPayload.avgByType?.test    || 0 },
            { Категорія: 'Гра',          Кількість: statsPayload.byType?.game    || 0, 'Середній результат %': statsPayload.avgByType?.game    || 0 },
            { Категорія: 'Квіз',         Кількість: statsPayload.byType?.quiz    || 0, 'Середній результат %': statsPayload.avgByType?.quiz    || 0 },
            { Категорія: 'Комплексний',  Кількість: statsPayload.byType?.complex || 0, 'Середній результат %': statsPayload.avgByType?.complex || 0 },
            { Категорія: 'Піца',         Кількість: statsPayload.byType?.pizza   || 0, 'Середній результат %': statsPayload.avgByType?.pizza   || 0 },
            { Категорія: 'Курси',        Кількість: statsPayload.byType?.course  || 0, 'Середній результат %': statsPayload.avgByType?.course  || 0 },
        ];
        const typeSheet = XLSX.utils.json_to_sheet(typeRows);
        autoWidth(typeSheet, typeRows);
        XLSX.utils.book_append_sheet(workbook, typeSheet, 'Категорії');

        if (Array.isArray(statsPayload.byCity) && statsPayload.byCity.length > 0) {
            const cityRows = statsPayload.byCity.map((e) => ({
                Місто: e.name, Всього: e.count, Успішно: e.passed,
                Провалено: e.failed, 'Успішність %': e.successRate, 'Середній результат %': e.avgPercentage
            }));
            const citySheet = XLSX.utils.json_to_sheet(cityRows);
            autoWidth(citySheet, cityRows);
            XLSX.utils.book_append_sheet(workbook, citySheet, 'Міста');
        }

        if (Array.isArray(statsPayload.byPosition) && statsPayload.byPosition.length > 0) {
            const posRows = statsPayload.byPosition.map((e) => ({
                Посада: e.name, Всього: e.count, Успішно: e.passed,
                Провалено: e.failed, 'Успішність %': e.successRate, 'Середній результат %': e.avgPercentage
            }));
            const posSheet = XLSX.utils.json_to_sheet(posRows);
            autoWidth(posSheet, posRows);
            XLSX.utils.book_append_sheet(workbook, posSheet, 'Посади');
        }

        if (trendData.length > 0) {
            const trendRows = trendData.map((e) => ({ Дата: e.date, Успішно: e.passed, Провалено: e.failed, Всього: e.total }));
            const trendSheet = XLSX.utils.json_to_sheet(trendRows);
            autoWidth(trendSheet, trendRows);
            XLSX.utils.book_append_sheet(workbook, trendSheet, 'Динаміка');
        }

        if (weakSpots.length > 0) {
            const weakRows = weakSpots.map((e) => ({ "Ім'я": e.student, Тип: getTypeLabel(e.type), 'Результат %': e.percentage, Дата: e.date }));
            const weakSheet = XLSX.utils.json_to_sheet(weakRows);
            autoWidth(weakSheet, weakRows);
            XLSX.utils.book_append_sheet(workbook, weakSheet, 'Слабкі місця');
        }

        if (recentResults.length > 0) {
            const recentRows = recentResults.map((e) => ({
                "Ім'я":       getResultFullName(e) || '—',
                Місто:         getResultCity(e)    || '—',
                Посада:        getResultPosition(e)|| '—',
                Тип:           getTypeLabel(e.type),
                Статус:        e.passed ? 'Успішно' : 'Провалено',
                'Результат %': e.percentage ?? (e.passed ? 100 : 0),
                Дата:          new Date(e.completedAt).toLocaleString('uk-UA'),
            }));
            const recentSheet = XLSX.utils.json_to_sheet(recentRows);
            autoWidth(recentSheet, recentRows);
            XLSX.utils.book_append_sheet(workbook, recentSheet, 'Останні результати');
        }

        const safeCity = cityLabel.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '') || 'all';
        const fileName = `dashboard_stats_${safeCity}_${period}d_${generatedAt.toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    };

    /* ── Heatmap data (unchanged) ── */
    const heatmapRows = statsPayload?.heatmapData
        ? Array.from(new Set(statsPayload.heatmapData.map(item => item.city || 'Невідомо')))
            .sort((a, b) => {
                if (a === 'Невідомо') return 1;
                if (b === 'Невідомо') return -1;
                return a.localeCompare(b, 'uk-UA');
            })
        : [];

    const heatmapCols = [
        { id: 'test',    label: 'Сервірування' },
        { id: 'game',    label: 'Гра'          },
        { id: 'quiz',    label: 'Квізи'        },
        { id: 'complex', label: 'Комплексні'   },
        { id: 'pizza',   label: 'Піца'         },
    ];

    /* ── Leaderboard split ── */
    const podium  = leaderboard.slice(0, 3);
    const restLB  = leaderboard.slice(3);

    const MEDAL_COLORS = ['#ffd700', '#c0c0c0', '#cd7f32'];
    const MEDAL_LABELS = ['🥇', '🥈', '🥉'];

    /* ── Loading state ── */
    if (loading) {
        return (
            <div className="dashboard-loading">
                <div className="loading-spinner" />
                <p>Завантаження статистики...</p>
            </div>
        );
    }

    /* ── Render ── */
    return (
        <div className="dashboard">

            {/* ── HERO HEADER ─────────────────────────────── */}
            <header className="hero-header">
                <div className="hero-left">
                    <div className="hero-avatar">
                        {user?.username?.charAt(0).toUpperCase() || <User size={28} />}
                    </div>
                    <div className="hero-greeting">
                        <span className="greeting-time">{getGreeting()},</span>
                        <h1 className="greeting-name">{user?.username || 'в ServIQ'}</h1>
                        <span className="greeting-sub">
                            <span className="status-pulse" />
                            Панель моніторингу · {period} днів
                        </span>
                    </div>
                </div>

                <div className="hero-quick-metrics">
                    <div className="quick-metric">
                        <span className="qm-value">{statsPayload?.totalResults ?? '—'}</span>
                        <span className="qm-label">Результатів</span>
                    </div>
                    <div className="quick-metric-divider" />
                    <div className="quick-metric">
                        <span className="qm-value qm-green">{statsPayload?.passedResults ?? '—'}</span>
                        <span className="qm-label">Успішно</span>
                    </div>
                    <div className="quick-metric-divider" />
                    <div className="quick-metric">
                        <span className="qm-value qm-orange">{statsPayload?.overallPercentage ?? '—'}%</span>
                        <span className="qm-label">Середній %</span>
                    </div>
                </div>

                <div className="hero-actions">
                    <button
                        className="btn-ai"
                        onClick={handleAiAnalyze}
                        disabled={aiLoading}
                        id="btn-ai-analyze"
                    >
                        {aiLoading
                            ? <><div className="btn-spinner" /> Аналіз...</>
                            : <><Sparkles size={16} /> AI Аналіз</>
                        }
                    </button>
                    <button
                        className="btn-export"
                        onClick={handleExportStats}
                        id="btn-export-xlsx"
                    >
                        <FileSpreadsheet size={16} /> Excel
                    </button>
                </div>
            </header>

            {/* ── FILTERS BAR ─────────────────────────────── */}
            <div className="filters-bar">
                <div className="filters-group">
                    <Clock size={14} className="filters-icon" />
                    {[7, 14, 30, 90].map(d => (
                        <button
                            key={d}
                            className={`pill-btn${period === d ? ' active' : ''}`}
                            onClick={() => setPeriod(d)}
                            id={`period-pill-${d}`}
                        >
                            {d} д
                        </button>
                    ))}
                </div>
                {user?.role === 'superadmin' && (
                    <div className="filters-group">
                        <MapPin size={14} className="filters-icon" />
                        <select
                            value={selectedCity}
                            onChange={(e) => setSelectedCity(e.target.value)}
                            className="city-select"
                            id="city-filter-select"
                        >
                            <option value="">Всі міста</option>
                            {cities.map(city => (
                                <option key={city} value={city}>{city}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* ── KPI RIBBON ──────────────────────────────── */}
            <div className="kpi-ribbon">
                {stats.map((stat, i) => (
                    <div key={i} className={`kpi-card kpi-${stat.color}`}>
                        <div className="kpi-icon-wrap">
                            {stat.icon}
                        </div>
                        <div className="kpi-body">
                            <span className="kpi-label">{stat.label}</span>
                            <span className="kpi-value">
                                <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                            </span>
                        </div>
                        <div className="kpi-glow" />
                    </div>
                ))}
            </div>

            {/* ── MOBILE TAB BAR ──────────────────────────── */}
            <div className="mobile-tabs">
                {[
                    { id: 'overview',     label: 'Огляд',   icon: <Activity size={18} /> },
                    { id: 'charts',       label: 'Графіки', icon: <BarChart2 size={18} /> },
                    { id: 'leaderboard',  label: 'Рейтинг', icon: <Trophy size={18} /> },
                ].map(tab => (
                    <button
                        key={tab.id}
                        className={`mobile-tab${activeTab === tab.id ? ' active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.icon}
                        <span>{tab.label}</span>
                    </button>
                ))}
            </div>

            {/* ── MAIN CONTENT ────────────────────────────── */}
            <div className="dashboard-content">

                {/* ── CHARTS SECTION ── */}
                <section className={`charts-section tab-section${activeTab === 'charts' || activeTab === 'overview' ? ' tab-visible' : ''}`}>

                    {/* Trend — full width */}
                    <div className="chart-card chart-full">
                        <div className="chart-card-header">
                            <Activity size={18} />
                            <h3>Динаміка результатів</h3>
                        </div>
                        <div className="chart-container">
                            <ResponsiveContainer width="100%" height={220}>
                                <AreaChart data={trendData}>
                                    <defs>
                                        <linearGradient id="gradPassed" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%"  stopColor="#4ade80" stopOpacity={0.35} />
                                            <stop offset="95%" stopColor="#4ade80" stopOpacity={0}    />
                                        </linearGradient>
                                        <linearGradient id="gradFailed" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%"  stopColor="#f87171" stopOpacity={0.35} />
                                            <stop offset="95%" stopColor="#f87171" stopOpacity={0}    />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.06)" />
                                    <XAxis dataKey="date"  stroke="var(--text-secondary)" fontSize={11} tickLine={false} axisLine={false} />
                                    <YAxis                 stroke="var(--text-secondary)" fontSize={11} tickLine={false} axisLine={false} />
                                    <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', fontSize: '0.85rem' }} itemStyle={{ color: '#fff' }} />
                                    <Area type="monotone" dataKey="passed" stroke="#4ade80" strokeWidth={2} fill="url(#gradPassed)" name="Успішно"   isAnimationActive animationDuration={1000} animationEasing="ease-out" />
                                    <Area type="monotone" dataKey="failed" stroke="#f87171" strokeWidth={2} fill="url(#gradFailed)" name="Провалено" isAnimationActive animationDuration={1000} animationEasing="ease-out" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Pie + Bar — side by side */}
                    <div className="charts-row">
                        <div className="chart-card">
                            <div className="chart-card-header">
                                <Target size={18} />
                                <h3>Успішність проходження</h3>
                            </div>
                            <div className="chart-container">
                                <ResponsiveContainer width="100%" height={250}>
                                    <PieChart>
                                        <Pie
                                            data={passingData}
                                            cx="50%" cy="50%"
                                            innerRadius={70} outerRadius={95}
                                            paddingAngle={6}
                                            dataKey="value"
                                            stroke="none"
                                            isAnimationActive animationDuration={1000} animationEasing="ease-out"
                                        >
                                            {passingData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                            <Label
                                                value={
                                                    passingData[0]?.value + passingData[1]?.value > 0
                                                        ? `${Math.round((passingData[0]?.value / (passingData[0]?.value + passingData[1]?.value)) * 100) || 0}%`
                                                        : '0%'
                                                }
                                                position="center"
                                                className="chart-center-label"
                                                fill="#fff"
                                            />
                                        </Pie>
                                        <Tooltip contentStyle={{ background: '#0f172a', border: 'none', borderRadius: '12px' }} itemStyle={{ color: '#fff' }} />
                                        <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="chart-card">
                            <div className="chart-card-header">
                                <Zap size={18} />
                                <h3>Активність за категоріями</h3>
                            </div>
                            <div className="chart-container">
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart data={categoryData}>
                                        <defs>
                                            <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%"   stopColor="#38bdf8" />
                                                <stop offset="100%" stopColor="#6366f1" />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.06)" />
                                        <XAxis dataKey="name" stroke="var(--text-secondary)" fontSize={11} tickLine={false} axisLine={false} />
                                        <YAxis               stroke="var(--text-secondary)" fontSize={11} tickLine={false} axisLine={false} />
                                        <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }} itemStyle={{ color: '#fff' }} />
                                        <Bar dataKey="value" fill="url(#barGrad)" radius={[6, 6, 0, 0]} isAnimationActive animationDuration={1000} animationEasing="ease-out" name="Кількість" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── HEATMAP ── */}
                <section className={`heatmap-section tab-section${activeTab === 'overview' || activeTab === 'charts' ? ' tab-visible' : ''}`}>
                    <div className="chart-card chart-full heatmap-card">
                        <div className="chart-card-header">
                            <MapPin size={18} />
                            <h3>Теплова карта успішності по містах та темах</h3>
                        </div>
                        <div className="heatmap-legend">
                            <span className="heatmap-legend-label">Низька</span>
                            <div className="heatmap-legend-bar" />
                            <span className="heatmap-legend-label">Висока</span>
                        </div>
                        <div className="heatmap-container">
                            {heatmapRows.length === 0 ? (
                                <p className="empty-msg">Немає даних для теплової карти за цей період</p>
                            ) : (
                                <div className="heatmap-grid-scroll">
                                    <table className="heatmap-table">
                                        <thead>
                                            <tr>
                                                <th>Місто / Філіал</th>
                                                {heatmapCols.map(col => (
                                                    <th key={col.id}>{col.label}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {heatmapRows.map(city => (
                                                <tr key={city} className="heatmap-row">
                                                    <td className="heatmap-city-label">
                                                        <MapPin size={13} className="city-icon" /> {city}
                                                    </td>
                                                    {heatmapCols.map(col => {
                                                        const cellData = statsPayload.heatmapData.find(
                                                            item => (item.city || 'Невідомо') === city && item.type === col.id
                                                        );
                                                        let colorClass = 'heatmap-cell-empty';
                                                        if (cellData && cellData.count > 0) {
                                                            const rate = cellData.successRate;
                                                            if (rate < 50)      colorClass = 'heatmap-cell-critical';
                                                            else if (rate < 70) colorClass = 'heatmap-cell-low';
                                                            else if (rate < 85) colorClass = 'heatmap-cell-medium';
                                                            else                colorClass = 'heatmap-cell-high';
                                                        }
                                                        return (
                                                            <td key={col.id} className="heatmap-cell-td">
                                                                <div className={`heatmap-cell ${colorClass}`}>
                                                                    <span className="heatmap-cell-value">
                                                                        {cellData ? `${cellData.successRate}%` : '—'}
                                                                    </span>
                                                                    <div className="heatmap-tooltip">
                                                                        <strong>{city} — {col.label}</strong>
                                                                        <div className="tooltip-divider" />
                                                                        <div>Спроб: {cellData ? cellData.count : 0}</div>
                                                                        <div>Успішність: {cellData ? `${cellData.successRate}%` : '—'}</div>
                                                                        <div>Середній бал: {cellData ? `${cellData.avgPercentage}%` : '—'}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* ── BOTTOM 3-COL GRID ── */}
                <section className="bottom-grid">

                    {/* LEADERBOARD */}
                    <div className={`panel leaderboard-panel tab-section${activeTab === 'leaderboard' || activeTab === 'overview' ? ' tab-visible' : ''}`}>
                        <div className="panel-header">
                            <Trophy size={18} />
                            <h3>ТОП-10 Рейтинг</h3>
                        </div>

                        {leaderboard.length === 0 ? (
                            <p className="empty-msg">Немає лідерів</p>
                        ) : (
                            <>
                                {/* Podium top-3 */}
                                <div className="podium">
                                    {/* Silver — rank 2 */}
                                    {podium[1] && (
                                        <div className="podium-card rank-2">
                                            <div className="podium-medal">{MEDAL_LABELS[1]}</div>
                                            <div className="podium-avatar" style={{ borderColor: MEDAL_COLORS[1] }}>
                                                {podium[1].studentName?.charAt(0).toUpperCase() || <User size={16} />}
                                            </div>
                                            <div className="podium-name">{podium[1].studentLastName} {podium[1].studentName}</div>
                                            <div className="podium-score" style={{ color: MEDAL_COLORS[1] }}>{podium[1].avgScore}%</div>
                                            <div className="podium-base rank-2-base" />
                                        </div>
                                    )}
                                    {/* Gold — rank 1 */}
                                    {podium[0] && (
                                        <div className="podium-card rank-1">
                                            <div className="podium-crown">👑</div>
                                            <div className="podium-medal">{MEDAL_LABELS[0]}</div>
                                            <div className="podium-avatar rank-1-avatar" style={{ borderColor: MEDAL_COLORS[0] }}>
                                                {podium[0].studentName?.charAt(0).toUpperCase() || <User size={20} />}
                                            </div>
                                            <div className="podium-name">{podium[0].studentLastName} {podium[0].studentName}</div>
                                            <div className="podium-score" style={{ color: MEDAL_COLORS[0] }}>{podium[0].avgScore}%</div>
                                            <div className="podium-base rank-1-base" />
                                        </div>
                                    )}
                                    {/* Bronze — rank 3 */}
                                    {podium[2] && (
                                        <div className="podium-card rank-3">
                                            <div className="podium-medal">{MEDAL_LABELS[2]}</div>
                                            <div className="podium-avatar" style={{ borderColor: MEDAL_COLORS[2] }}>
                                                {podium[2].studentName?.charAt(0).toUpperCase() || <User size={16} />}
                                            </div>
                                            <div className="podium-name">{podium[2].studentLastName} {podium[2].studentName}</div>
                                            <div className="podium-score" style={{ color: MEDAL_COLORS[2] }}>{podium[2].avgScore}%</div>
                                            <div className="podium-base rank-3-base" />
                                        </div>
                                    )}
                                </div>

                                {/* Compact list ranks 4–10 */}
                                {restLB.length > 0 && (
                                    <div className="lb-rest">
                                        {restLB.map((item, idx) => (
                                            <div key={idx} className="lb-rest-row">
                                                <span className="lb-rest-rank">#{idx + 4}</span>
                                                <div className="lb-rest-avatar">
                                                    {item.studentName?.charAt(0).toUpperCase() || <User size={14} />}
                                                </div>
                                                <span className="lb-rest-name">{item.studentLastName} {item.studentName}</span>
                                                {item.studentCity && (
                                                    <span className="lb-rest-city"><MapPin size={11} /> {item.studentCity}</span>
                                                )}
                                                <span className="lb-rest-score">{item.avgScore}%</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* WEAK SPOTS + ERRORS */}
                    <div className={`panel weak-panel tab-section${activeTab === 'overview' ? ' tab-visible' : ''}`}>
                        <div className="panel-header">
                            <AlertCircle size={18} className="icon-red" />
                            <h3>Потрібно повторити</h3>
                        </div>
                        {weakSpots.length === 0 ? (
                            <p className="empty-msg">Поки немає даних</p>
                        ) : (
                            <div className="weak-list">
                                {weakSpots.map((ws, i) => (
                                    <div key={i} className="weak-row">
                                        <div className="weak-info">
                                            <span className="weak-name">{ws.student}</span>
                                            <span className={`type-badge ${getTypeBadgeClass(ws.type)}`}>{getTypeLabel(ws.type)}</span>
                                        </div>
                                        <div className="weak-right">
                                            <span className="weak-score">{ws.percentage}%</span>
                                            <span className="weak-date">{ws.date}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Error analytics inline */}
                        {statsPayload?.errorAnalytics && (
                            statsPayload.errorAnalytics.deskErrors.length > 0 ||
                            statsPayload.errorAnalytics.quizErrors.length > 0
                        ) && (
                            <div className="errors-section">
                                <div className="panel-header" style={{ marginTop: '1.5rem' }}>
                                    <AlertCircle size={18} className="icon-orange" />
                                    <h3>Типові помилки</h3>
                                </div>
                                {statsPayload.errorAnalytics.deskErrors.length > 0 && (
                                    <div className="error-group">
                                        <h4>Сервірування</h4>
                                        <ul className="error-list">
                                            {statsPayload.errorAnalytics.deskErrors.map((err, idx) => (
                                                <li key={idx} className="error-item">
                                                    <span className="error-name">{err.name}</span>
                                                    <span className="error-count-badge">{err.count} разів</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {statsPayload.errorAnalytics.quizErrors.length > 0 && (
                                    <div className="error-group" style={{ marginTop: '1rem' }}>
                                        <h4>Квізи</h4>
                                        <ul className="error-list">
                                            {statsPayload.errorAnalytics.quizErrors.map((err, idx) => (
                                                <li key={idx} className="error-item">
                                                    <span className="error-name">«{err.question}»</span>
                                                    <span className="error-count-badge failed">{err.count} помилок</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ACTIVITY FEED — recent results */}
                    <div className={`panel activity-panel tab-section${activeTab === 'overview' ? ' tab-visible' : ''}`}>
                        <div className="panel-header">
                            <ClipboardList size={18} />
                            <h3>Останні результати {isAdminCityOnly && `(${user.city})`}</h3>
                        </div>
                        {recentResults.length === 0 ? (
                            <p className="empty-msg">Результатів поки немає</p>
                        ) : (
                            <div className="activity-feed">
                                {recentResults.map((r, i) => (
                                    <div key={i} className="activity-card">
                                        <div className={`activity-avatar ${r.passed ? 'av-pass' : 'av-fail'}`}>
                                            {(getResultFirstName(r)?.charAt(0) || '?').toUpperCase()}
                                        </div>
                                        <div className="activity-body">
                                            <div className="activity-top">
                                                <span className="activity-name">{getResultFullName(r) || '—'}</span>
                                                <span className={`activity-status ${r.passed ? 'status-pass' : 'status-fail'}`}>
                                                    {r.passed ? '✓ Успішно' : '✗ Провалено'}
                                                </span>
                                            </div>
                                            <div className="activity-meta">
                                                {getResultCity(r) && (
                                                    <span className="meta-chip"><MapPin size={11} /> {getResultCity(r)}</span>
                                                )}
                                                <span className={`meta-chip type-chip ${getTypeBadgeClass(r.type)}`}>{getTypeLabel(r.type)}</span>
                                                {r.percentage != null && (
                                                    <span className="meta-chip">{r.percentage}%</span>
                                                )}
                                            </div>
                                        </div>
                                        <span className="activity-time"><Clock size={11} /> {timeAgo(r.completedAt)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                </section>
            </div>

            {/* ── AI SIDE PANEL ──────────────────────────── */}
            {aiOpen && (
                <div className="ai-overlay" onClick={() => setAiOpen(false)}>
                    <aside className="ai-side-panel" onClick={e => e.stopPropagation()}>
                        <div className="ai-panel-header">
                            <div className="ai-panel-title">
                                <Sparkles size={18} />
                                <span>AI Аналізатор</span>
                                {aiLoading && <div className="ai-thinking-dots"><span /><span /><span /></div>}
                            </div>
                            <button className="ai-close-btn" onClick={() => setAiOpen(false)} id="btn-ai-close">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="ai-panel-body">
                            {aiLoading ? (
                                <div className="ai-loading-state">
                                    <div className="ai-loader-ring" />
                                    <p>AI аналізує дані платформи...</p>
                                </div>
                            ) : (
                                <>
                                    {aiSummary?.scope && (
                                        <div className="ai-scope-row">
                                            <span className="ai-scope-tag">{aiSummary.scope.period}</span>
                                            <span className="ai-scope-tag">{aiSummary.scope.city}</span>
                                            <span className="ai-scope-tag">{aiSummary.scope.module}</span>
                                            <span className="ai-scope-tag">{aiSummary.totalResults} результатів</span>
                                            {aiSummary.sampleQuality?.level && (
                                                <span className="ai-scope-tag">Вибірка: {aiSummary.sampleQuality.level}</span>
                                            )}
                                        </div>
                                    )}
                                    <AiReportView content={aiAnalysis} />
                                </>
                            )}
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
